import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {AccountService} from '../../../services/account.service';
import {AccountDTO} from '../../../model/accountDTO';
import {NotificationService} from '../../../services/notification.service';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ValidateAccountNumber} from '../../../validators/iban.validator';
import {ValidateDate} from '../../../validators/date.validator';
import VALUTA_REGEX from '../../../validators/regex/valuta.regex';
import {ManualImportDTO} from '../../../model/manualImportDTO';
import {ManualTransactionDTO} from '../../../model/manualTransactionDTO';
import {TransactionService} from '../../../services/transaction.service';
import {CurrencyService} from '../../../services/currency-service';
import * as moment from 'moment';
import {InformDTO} from '../../shared/notification/InformDTO';
import {BalanceDTO} from '../../../model/balanceDTO';

@Component({
  templateUrl: './manual-import.component.html',
  styleUrls: ['./manual-import.component.scss']
})
export class ManualImportComponent implements OnInit {

  submitted = false;
  balance: BalanceDTO;
  account: AccountDTO;
  public formGroup: FormGroup;
  private isLoading = false;

  constructor(private router: Router,
              public currencyService: CurrencyService,
              private route: ActivatedRoute,
              private formBuilder: FormBuilder,
              private notificationService: NotificationService,
              private service: TransactionService,
              private accountService: AccountService) {
  }

  get f() {
    return this.formGroup.controls;
  }

  ngOnInit(): void {
    this.route.params.subscribe(
      params => this.getAccount(params['accountId'])
    );
    this.formGroup = this.formBuilder.group({
      closingBalance: ['', [Validators.required, Validators.pattern(VALUTA_REGEX)]],
      transactions: this.formBuilder.array([this.createTransaction()])
    });
  }

  addTransaction(): void {
    this.getTransactions().push(this.createTransaction());
  }

  getTransactions(): FormArray {
    return <FormArray>this.formGroup.get('transactions');
  }

  deleteTransaction(index: number): void {
   if (this.isRowEmpty(index)) {
      this.getTransactions().removeAt(index);
    } else {
      this.notificationService.confirm({header: 'wil je de transactie verwijderen?', ok: 'verwijderen'}).then(() => {
        this.getTransactions().removeAt(index);
        this.addRowIfNeeded();
      });
    }
    this.addRowIfNeeded();
  }

  private addRowIfNeeded(): void {
    if (this.getTransactions().length === 0) {
      this.addTransaction();
    }
  }

  private getAccount(accountId: number): void {
    this.accountService.get(accountId).subscribe(account => {
        this.accountService.getLastBalance(account.id).subscribe(balance => this.balance = balance);
        this.account = account;
      }, error => this.handleNoAuthority(error));
  }

  private handleNoAuthority(response: any): void {
    this.notificationService.errorResponse(response);
    this.router.navigate(['/']);
  }

  private createTransaction(): FormGroup {
    return this.formBuilder.group({
      date: ['', [Validators.required, ValidateDate]],
      amount: ['', [Validators.required, Validators.pattern(VALUTA_REGEX)]],
      iban: ['', [ValidateAccountNumber]],
      description: ['', [Validators.required]],
    });
  }

  manualImport(): void {
    this.submitted = true;
    this.deleteEmptyRows();
    if (!this.formGroup.valid || this.isLoading) {
      return;
    }
    const dto = this.mapToDto(this.formGroup.value);
    this.service.manualImport(dto).subscribe(response => {
        this.notificationService.success(response.added + ' transacties toegevoegd');
        this.router.navigate([`/rekening/${this.account.id}`]);
      }, error => {
        if (error.error.message.startsWith('MANUAL_IMPORT_BALANCE_MISMATCH'))
          this.handleBalanceMismatch(error);
        if (error.error.message === 'TRANSACTION_BEFORE_LAST') {
          this.handleTransactionBeforeLast();
        }
      }
    );
  }

  private handleBalanceMismatch(error): void {
    const params = error.error.message.split(' ');
    const closing = params[1];
    const opening = params[2];
    const f = params[3];
    const c = params[4];
    this.notificationService.inform({
      header: 'er is een foutje gemaakt',
      body: this.currencyService.euro(closing) + '(eindsaldo) - ' +
        this.currencyService.euro(opening) + '(startsaldo) = ' + this.currencyService.euro(f) + ' <br>' +
        'dit is niet gelijk aan de som van de transacties ' + this.currencyService.euro(c) + '<br>' +
        '<b>controleer of alle transacties zijn ingevoerd</b>',
      ok: 'gegevens controleren'
    });
  }

  private mapToDto(value: any): ManualImportDTO {
    const request: ManualImportDTO = {};
    request.accountId = this.account.id;
    request.closingBalance = value.closingBalance;
    request.transactions = value.transactions.map(transaction => ({
      iban: transaction.iban,
      date: transaction.date,
      amount: transaction.amount,
      description: transaction.description
    } as ManualTransactionDTO));
    return request;
  }

  private deleteEmptyRows(): void {
    const rowsToDelete = [];
    for (let i = 0; i < this.getTransactions().length; i++) {
      if (this.isRowEmpty(i)) {
        rowsToDelete.push(i);
      }
    }
    rowsToDelete.sort();
    rowsToDelete.reverse();
    rowsToDelete.forEach(index => this.getTransactions().removeAt(index));
    this.addRowIfNeeded();
  }

  private isRowEmpty(index: number): boolean {
    const {date, amount, iban, description} = this.getTransactions().controls[index].value;
    return date === '' && amount === '' && iban === '' && description === '';
  }

  private handleTransactionBeforeLast() {
    const data: InformDTO = {
      header: 'transacties mogen niet ouder zijn dan de laatst geïmporteerde transactie',
      body: 'neem contact met ons op via de chat rechtsonder of door te mailen naar <a class="link" href="mailto:info@reveon.nl">info@reveon.nl</a>.'
    };
    this.notificationService.inform(data);
  }
}
